import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Paper, Box, Button, Grid, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Divider, CircularProgress, Alert, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, FormControl, InputLabel,
  Select, MenuItem, InputAdornment
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import PaymentIcon from '@mui/icons-material/Payment';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { formatAddress } from '../../utils/addressFormatter';
import { downloadInvoicePDF, openInvoicePDF } from '../../utils/pdfInvoice';
import { downloadInvoiceCSV } from '../../utils/invoiceUtils';
import { notifyBySeverity } from '../../utils/toastUtils';
import { getApiErrorMessage } from '../../utils/errorUtils';

// Get API URL from environment variables
import { API_URL } from '../../utils/apiConfig';
import { formatCurrencySync, getCurrencySymbol, setCurrencyCache } from '../../utils/currencyUtils';
import { formatDateLocalizedSync } from '../../utils/dateTimeUtils';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { formatFailedActionMessage, formatSuccessActionMessage } from '../../utils/uiMessages';

const ViewInvoice = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    method: 'cash',
    reference: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [editPaymentDialog, setEditPaymentDialog] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editPaymentData, setEditPaymentData] = useState({
    amount: 0,
    method: 'cash',
    reference: '',
    notes: ''
  });
  const [downloadDialog, setDownloadDialog] = useState(false);
  const [deleteInvoiceOpen, setDeleteInvoiceOpen] = useState(false);
  const [deletePaymentOpen, setDeletePaymentOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);

  useEffect(() => {
    if (!notification.open || !notification.message) return;
    notifyBySeverity(notification.message, notification.severity);

    setNotification(prev => ({ ...prev, open: false }));
  }, [notification]);
  
  useEffect(() => {
    const fetchInvoice = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Get token from localStorage
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Authentication token not found');
        }

        const response = await axios.get(`${API_URL}/billing/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setInvoice(response.data.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching invoice:', err);
        setError(formatFailedActionMessage('load', 'invoice'));
        setLoading(false);
      }
    };
    
    fetchInvoice();
  }, [id]);

  // Load organization to enrich PDFs with practice info from Settings
  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Authentication token not found');
        }

        const response = await axios.get(`${API_URL}/organizations/my`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (response.data.success) {
          const org = response.data.data;
          setOrganization(org);
          // Use org currency everywhere on this page so rupee doesn't appear when org is e.g. ILS
          if (org?.settings?.currency) {
            setCurrencyCache(org.settings.currency);
          }
        } else {
          console.warn('Failed to load organization for invoice PDF:', response.data);
        }
      } catch (err) {
        console.error('Error fetching organization for invoice PDF:', err);
      }
    };

    fetchOrganization();
  }, []);
  
  // Format currency using organization settings (prefer org currency when loaded)
  const currencyCode = organization?.settings?.currency || undefined;
  const formatCurrency = (amount) => {
    return formatCurrencySync(amount, currencyCode);
  };

  // Get currency symbol for InputAdornment (same source as formatCurrency)
  const currencySymbol = getCurrencySymbol(currencyCode);
  
  // Format date using organization settings (date format + timezone)
  const formatDate = (date) => formatDateLocalizedSync(date);
  
  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid':
        return 'success';
      case 'Partial':
        return 'warning';
      case 'Overdue':
        return 'error';
      case 'Pending':
        return 'info';
      default:
        return 'default';
    }
  };

  // Build enriched invoice object for PDF: prefer selected clinic for Billed By; fallback to organization
  const buildInvoiceForPdf = () => {
    if (!invoice) return null;

    const clinic = invoice.clinic || {};
    const hasClinic = clinic.name || (clinic.address && (clinic.address.street || clinic.address.city));

    let billedBy;
    if (hasClinic) {
      billedBy = {
        name: clinic.name,
        address: clinic.address ? { street: clinic.address.street, city: clinic.address.city, state: clinic.address.state, pincode: clinic.address.pincode } : {},
        phone: clinic.phone,
        email: clinic.email,
        contactInfo: clinic.phone ? { phone: clinic.phone } : {},
        ...(clinic.email && { contactInfo: { ...(clinic.contactInfo || {}), email: clinic.email } })
      };
    } else if (organization) {
      const practiceInfo = organization.settings?.practiceInfo || {};
      billedBy = {
        name: practiceInfo.name || organization.name,
        address: { street: practiceInfo.address || organization.address?.street, city: organization.address?.city, state: organization.address?.state, pincode: organization.address?.pincode },
        phone: practiceInfo.phone || organization.contactInfo?.phone,
        email: practiceInfo.email || organization.contactInfo?.email,
        contactInfo: organization.contactInfo || {}
      };
    } else {
      billedBy = { name: clinic.name, address: clinic.address || {}, phone: clinic.phone, email: clinic.email };
    }

    return {
      ...invoice,
      organization: billedBy,
      contactEmail: billedBy.email,
      contactPhone: billedBy.phone
    };
  };
  
  const handlePrint = async () => {
    try {
      const invoiceForPdf = buildInvoiceForPdf();
      await openInvoicePDF(invoiceForPdf || invoice);
    } catch (error) {
      console.error('Error printing invoice:', error);
      setNotification({
        open: true,
        message: formatFailedActionMessage('print', 'invoice'),
        severity: 'error'
      });
    }
  };
  
  const handleDownload = async () => {
    setDownloadDialog(true);
  };

  const handleDownloadFormat = async (format) => {
    try {
      if (format === 'pdf') {
        const invoiceForPdf = buildInvoiceForPdf();
        await downloadInvoicePDF(invoiceForPdf || invoice);
      } else {
        downloadInvoiceCSV(invoice);
      }
      setDownloadDialog(false);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      setNotification({
        open: true,
        message: formatFailedActionMessage('download', 'invoice'),
        severity: 'error'
      });
    }
  };
  
  const handleSendEmail = async () => {
    try {
      if (!invoice) return;
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      await axios.post(
        `${API_URL}/billing/${id}/email`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setNotification({
        open: true,
        message: 'Invoice sent to patient via email',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error sending invoice email:', error);
      setNotification({
        open: true,
        message: getApiErrorMessage(error, formatFailedActionMessage('send', 'invoice email')),
        severity: 'error'
      });
    }
  };
  
  const handleRecordPayment = () => {
    setPaymentData({
      amount: invoice.balanceAmount || invoice.totalAmount,
      method: 'cash',
      reference: '',
      notes: ''
    });
    setPaymentDialog(true);
  };

  const handlePaymentInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePaymentSubmit = async () => {
    if (paymentData.amount <= 0) {
      setNotification({
        open: true,
        message: 'Payment amount must be greater than zero',
        severity: 'error'
      });
      return;
    }

    if (paymentData.amount > (invoice.balanceAmount || invoice.totalAmount)) {
      setNotification({
        open: true,
        message: 'Payment amount cannot exceed the balance due',
        severity: 'error'
      });
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      await axios.post(`${API_URL}/billing/${id}/payment`, {
        amount: paymentData.amount,
        paymentMethod: paymentData.method,
        reference: paymentData.reference,
        notes: paymentData.notes
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setNotification({
        open: true,
        message: formatSuccessActionMessage('recorded', 'Payment'),
        severity: 'success'
      });

      // Refresh invoice data
      const updatedResponse = await axios.get(`${API_URL}/billing/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setInvoice(updatedResponse.data.data);

      setPaymentDialog(false);
    } catch (err) {
      console.error('Error recording payment:', err);
      setNotification({
        open: true,
        message: getApiErrorMessage(err, formatFailedActionMessage('record', 'payment')),
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setEditPaymentData({
      amount: payment.amount,
      method: payment.paymentMethod || 'cash',
      reference: payment.transactionId || '',
      notes: payment.notes || ''
    });
    setEditPaymentDialog(true);
  };

  const handleDeletePaymentRequest = (payment) => {
    setPaymentToDelete(payment);
    setDeletePaymentOpen(true);
  };

  const confirmDeletePayment = async () => {
    if (!paymentToDelete) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      await axios.delete(`${API_URL}/billing/${id}/payment/${paymentToDelete._id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setNotification({
        open: true,
        message: formatSuccessActionMessage('deleted', 'Payment'),
        severity: 'success'
      });

      // Refresh invoice data
      const updatedResponse = await axios.get(`${API_URL}/billing/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setInvoice(updatedResponse.data.data);
      setDeletePaymentOpen(false);
      setPaymentToDelete(null);
    } catch (err) {
      console.error('Error deleting payment:', err);
      setNotification({
        open: true,
        message: getApiErrorMessage(err, formatFailedActionMessage('delete', 'payment')),
        severity: 'error'
      });
    }
  };

  const handleEditPaymentInputChange = (e) => {
    const { name, value } = e.target;
    setEditPaymentData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditPaymentSubmit = async () => {
    if (editPaymentData.amount <= 0) {
      setNotification({
        open: true,
        message: 'Payment amount must be greater than zero',
        severity: 'error'
      });
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      await axios.put(`${API_URL}/billing/${id}/payment/${editingPayment._id}`, {
        amount: editPaymentData.amount,
        paymentMethod: editPaymentData.method,
        reference: editPaymentData.reference,
        notes: editPaymentData.notes
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setNotification({
        open: true,
        message: formatSuccessActionMessage('updated', 'Payment'),
        severity: 'success'
      });

      // Refresh invoice data
      const updatedResponse = await axios.get(`${API_URL}/billing/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setInvoice(updatedResponse.data.data);

      setEditPaymentDialog(false);
    } catch (err) {
      console.error('Error updating payment:', err);
      setNotification({
        open: true,
        message: getApiErrorMessage(err, formatFailedActionMessage('update', 'payment')),
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = () => {
    navigate(`/billing/invoice/${id}/edit`);
  };

  const handleDelete = () => {
    setDeleteInvoiceOpen(true);
  };

  const confirmDeleteInvoice = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      await axios.delete(`${API_URL}/billing/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setNotification({
        open: true,
        message: formatSuccessActionMessage('deleted', 'Invoice'),
        severity: 'success'
      });
      setDeleteInvoiceOpen(false);
      navigate('/billing');
    } catch (err) {
      console.error('Error deleting invoice:', err);
      setNotification({
        open: true,
        message: formatFailedActionMessage('delete', 'invoice'),
        severity: 'error'
      });
    }
  };
  
  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4, mb: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </Container>
    );
  }
  
  if (!invoice) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4, mb: 4 }}>
          <Alert severity="warning">Invoice not found.</Alert>
        </Box>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg" className="invoice-container">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item>
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
          </Grid>
          <Grid item>
            <Typography variant="h4" component="h1">
              Invoice #{invoice.invoiceNumber}
            </Typography>
          </Grid>
          <Grid item>
            <Chip 
              label={invoice.paymentStatus} 
              color={getStatusColor(invoice.paymentStatus)} 
              sx={{ ml: 2 }}
            />
          </Grid>
          <Grid item sx={{ flexGrow: 1 }} />
          <Grid item>
            <Button 
              startIcon={<PrintIcon />} 
              variant="outlined" 
              onClick={handlePrint}
              sx={{ mr: 1 }}
            >
              Print
            </Button>
          </Grid>
          <Grid item>
            <Button 
              startIcon={<DownloadIcon />} 
              variant="outlined" 
              onClick={handleDownload}
              sx={{ mr: 1 }}
            >
              Download
            </Button>
          </Grid>
                     <Grid item>
             <Button 
               startIcon={<EmailIcon />} 
               variant="outlined" 
               onClick={handleSendEmail}
               sx={{ mr: 1 }}
             >
               Email
             </Button>
           </Grid>
           <Grid item>
             <Button 
               startIcon={<EditIcon />} 
               variant="outlined" 
               onClick={handleEdit}
               sx={{ mr: 1 }}
             >
               Edit
             </Button>
           </Grid>
           <Grid item>
             <Button 
               startIcon={<DeleteIcon />} 
               variant="outlined" 
               color="error" 
               onClick={handleDelete}
               sx={{ mr: 1 }}
             >
               Delete
             </Button>
           </Grid>
           {invoice.paymentStatus !== 'Paid' && (
             <Grid item>
               <Button 
                 startIcon={<PaymentIcon />} 
                 variant="contained" 
                 color="primary" 
                 onClick={handleRecordPayment}
               >
                 Record Payment
               </Button>
             </Grid>
           )}
        </Grid>
        
        <Paper sx={{ p: 4, mb: 3 }} className="invoice-paper">
          {/* Invoice Header */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                From
              </Typography>
              <Typography variant="body1">
                {invoice.clinic.name}
              </Typography>
              <Typography variant="body2">
                {formatAddress(invoice.clinic.address)}
              </Typography>
              <Typography variant="body2">
                Phone: {invoice.clinic.phone}
              </Typography>
              {invoice.isGstInvoice && (
                <Typography variant="body2">
                  GST: {invoice.gstDetails.gstNumber}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6} sx={{ textAlign: { md: 'right' } }}>
              <Typography variant="h6" gutterBottom>
                To
              </Typography>
              <Typography variant="body1">
                {invoice.patient.name}
              </Typography>
              <Typography variant="body2">
                {formatAddress(invoice.patient.address)}
              </Typography>
              <Typography variant="body2">
                Phone: {invoice.patient.phone}
              </Typography>
              <Typography variant="body2">
                Email: {invoice.patient.email}
              </Typography>
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 3 }} />
          
          {/* Invoice Details */}
          <Grid container spacing={3}>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="textSecondary">
                Invoice Number
              </Typography>
              <Typography variant="body1">
                {invoice.invoiceNumber}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="textSecondary">
                Invoice Date
              </Typography>
              <Typography variant="body1">
                {formatDate(invoice.invoiceDate)}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="textSecondary">
                Due Date
              </Typography>
              <Typography variant="body1">
                {formatDate(invoice.dueDate)}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="textSecondary">
                Status
              </Typography>
              <Chip 
                label={invoice.paymentStatus} 
                color={getStatusColor(invoice.paymentStatus)} 
                size="small"
              />
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Items
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Discount</TableCell>
                    <TableCell align="right">Tax</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoice.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.description}</TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                      <TableCell align="right">{item.discount}%</TableCell>
                      <TableCell align="right">{item.tax}%</TableCell>
                      <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          
          <Grid container spacing={2} sx={{ mt: 3 }}>
            <Grid item xs={12} md={6}>
              {invoice.notes && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Notes
                  </Typography>
                  <Typography variant="body2">
                    {invoice.notes}
                  </Typography>
                </Box>
              )}
              
              <Typography variant="subtitle2" gutterBottom>
                Terms and Conditions
              </Typography>
              <Typography variant="body2">
                {invoice.termsAndConditions}
              </Typography>
              
              {invoice.isGstInvoice && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    GST Details
                  </Typography>
                  <Typography variant="body2">
                    HSN/SAC: {invoice.gstDetails.hsnCode}
                  </Typography>
                  <Typography variant="body2">
                    CGST: {invoice.gstDetails.cgst}% | SGST: {invoice.gstDetails.sgst}% | IGST: {invoice.gstDetails.igst}%
                  </Typography>
                </Box>
              )}
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      Subtotal:
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="body2">
                      {formatCurrency(invoice.subtotal)}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      Discount:
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="body2">
                      {formatCurrency(invoice.discountAmount)}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      Tax:
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="body2">
                      {formatCurrency(invoice.taxAmount)}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Total:
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {formatCurrency(invoice.totalAmount)}
                    </Typography>
                  </Grid>
                  
                                     <Grid item xs={6}>
                     <Typography variant="body2">
                       Amount Paid:
                     </Typography>
                   </Grid>
                   <Grid item xs={6} sx={{ textAlign: 'right' }}>
                     <Typography variant="body2">
                       {formatCurrency(invoice.amountPaid || 0)}
                     </Typography>
                   </Grid>
                   
                   <Grid item xs={6}>
                     <Typography variant="body2" fontWeight="bold">
                       Balance Due:
                     </Typography>
                   </Grid>
                   <Grid item xs={6} sx={{ textAlign: 'right' }}>
                     <Typography variant="body2" fontWeight="bold">
                       {formatCurrency(invoice.balanceAmount || invoice.totalAmount)}
                     </Typography>
                   </Grid>
                </Grid>
              </Box>
              
                             {(invoice.payments && invoice.payments.length > 0) && (
                 <Box sx={{ mt: 3 }}>
                   <Typography variant="subtitle2" gutterBottom>
                     Payment History
                   </Typography>
                   <TableContainer>
                     <Table size="small">
                                               <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Method</TableCell>
                            <TableCell>Reference</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell align="center">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {invoice.payments.map((payment, index) => (
                            <TableRow key={index}>
                              <TableCell>{formatDate(payment.paymentDate || payment.date)}</TableCell>
                              <TableCell>{payment.paymentMethod || payment.method}</TableCell>
                              <TableCell>{payment.transactionId || payment.reference}</TableCell>
                              <TableCell align="right">{formatCurrency(payment.amount)}</TableCell>
                              <TableCell align="center">
                                <IconButton 
                                  size="small" 
                                  color="primary" 
                                  onClick={() => handleEditPayment(payment)}
                                  title="Edit Payment"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton 
                                  size="small" 
                                  color="error" 
                                  onClick={() => handleDeletePaymentRequest(payment)}
                                  title="Delete Payment"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                     </Table>
                   </TableContainer>
                 </Box>
               )}
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Payment Dialog */}
      <Dialog open={paymentDialog} onClose={() => setPaymentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Invoice: {invoice?.invoiceNumber} | Balance Due: {formatCurrency(invoice?.balanceAmount || invoice?.totalAmount || 0)}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Payment Amount *"
                type="number"
                name="amount"
                value={paymentData.amount}
                onChange={handlePaymentInputChange}
                inputProps={{ min: 0, max: invoice?.balanceAmount || invoice?.totalAmount, step: 0.01 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Payment Method *</InputLabel>
                <Select
                  name="method"
                  value={paymentData.method}
                  label="Payment Method *"
                  onChange={handlePaymentInputChange}
                >
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="credit card">Credit Card</MenuItem>
                  <MenuItem value="debit card">Debit Card</MenuItem>
                  <MenuItem value="upi">UPI</MenuItem>
                  <MenuItem value="bank transfer">Bank Transfer</MenuItem>
                  <MenuItem value="cheque">Cheque</MenuItem>
                  <MenuItem value="insurance">Insurance</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reference Number"
                name="reference"
                value={paymentData.reference}
                onChange={handlePaymentInputChange}
                placeholder="Transaction ID, Cheque Number, etc."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={paymentData.notes}
                onChange={handlePaymentInputChange}
                multiline
                rows={2}
                placeholder="Additional notes about this payment"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialog(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button 
            onClick={handlePaymentSubmit} 
            variant="contained" 
            disabled={submitting}
          >
            {submitting ? 'Recording...' : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Edit Dialog */}
      <Dialog open={editPaymentDialog} onClose={() => setEditPaymentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Payment</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Invoice: {invoice?.invoiceNumber} | Original Amount: {formatCurrency(editingPayment?.amount || 0)}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Payment Amount *"
                type="number"
                name="amount"
                value={editPaymentData.amount}
                onChange={handleEditPaymentInputChange}
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Payment Method *</InputLabel>
                <Select
                  name="method"
                  value={editPaymentData.method}
                  label="Payment Method *"
                  onChange={handleEditPaymentInputChange}
                >
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="credit card">Credit Card</MenuItem>
                  <MenuItem value="debit card">Debit Card</MenuItem>
                  <MenuItem value="upi">UPI</MenuItem>
                  <MenuItem value="bank transfer">Bank Transfer</MenuItem>
                  <MenuItem value="cheque">Cheque</MenuItem>
                  <MenuItem value="insurance">Insurance</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reference Number"
                name="reference"
                value={editPaymentData.reference}
                onChange={handleEditPaymentInputChange}
                placeholder="Transaction ID, Cheque Number, etc."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={editPaymentData.notes}
                onChange={handleEditPaymentInputChange}
                multiline
                rows={2}
                placeholder="Additional notes about this payment"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditPaymentDialog(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleEditPaymentSubmit} 
            variant="contained" 
            disabled={submitting}
          >
            {submitting ? 'Updating...' : 'Update Payment'}
          </Button>
        </DialogActions>
             </Dialog>

       {/* Download Format Dialog */}
       <Dialog open={downloadDialog} onClose={() => setDownloadDialog(false)} maxWidth="sm" fullWidth>
         <DialogTitle>Choose Download Format</DialogTitle>
         <DialogContent>
           <Typography variant="body1" sx={{ mb: 2 }}>
             Select the format you would like to download the invoice:
           </Typography>
           <Grid container spacing={2}>
             <Grid item xs={6}>
               <Button
                 fullWidth
                 variant="outlined"
                 size="large"
                 onClick={() => handleDownloadFormat('pdf')}
                 sx={{ 
                   height: 80, 
                   flexDirection: 'column',
                   border: '2px solid #1976d2',
                   '&:hover': { border: '2px solid #1565c0' }
                 }}
               >
                 <Typography variant="h6" color="primary">PDF</Typography>
                 <Typography variant="body2" color="text.secondary">
                   Professional format
                 </Typography>
               </Button>
             </Grid>
             <Grid item xs={6}>
               <Button
                 fullWidth
                 variant="outlined"
                 size="large"
                 onClick={() => handleDownloadFormat('csv')}
                 sx={{ 
                   height: 80, 
                   flexDirection: 'column',
                   border: '2px solid #2e7d32',
                   '&:hover': { border: '2px solid #1b5e20' }
                 }}
               >
                 <Typography variant="h6" color="success.main">CSV</Typography>
                 <Typography variant="body2" color="text.secondary">
                   Data analysis
                 </Typography>
               </Button>
             </Grid>
           </Grid>
         </DialogContent>
         <DialogActions>
           <Button onClick={() => setDownloadDialog(false)}>
             Cancel
           </Button>
         </DialogActions>
       </Dialog>
      <ConfirmDialog
        open={deleteInvoiceOpen}
        title="Delete invoice"
        message="Are you sure you want to delete this invoice? This action cannot be undone."
        confirmText="Delete"
        confirmColor="error"
        onClose={() => setDeleteInvoiceOpen(false)}
        onConfirm={confirmDeleteInvoice}
      />
      <ConfirmDialog
        open={deletePaymentOpen}
        title="Delete payment"
        message="Are you sure you want to delete this payment? This action cannot be undone."
        confirmText="Delete"
        confirmColor="error"
        onClose={() => {
          setDeletePaymentOpen(false);
          setPaymentToDelete(null);
        }}
        onConfirm={confirmDeletePayment}
      />
 
    </Container>
  );
};

export default ViewInvoice;